import { Dispatch, useRef } from 'react'
import omit from 'lodash/omit'

import { getUserId as getSlackUserId } from '../../../../services/api/slack'
import { getUserId as getMsUserId } from '../../../../services/api/microsoft'
import { getUserId as getGoogleUserId } from '../../../../services/api/google'
import { SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES } from '@vacationtracker/shared/data/slack'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { ICheckUserId } from '@vacationtracker/shared/types/company'
import { Platform } from '@vacationtracker/shared/types/core-event'
import { ISlackOpenidTokenResponse } from '@vacationtracker/shared/types/repository/slack-api-repository'

import { BrowserRuntimeV2 } from '../../../../util/openid/browser-runtime-v2'
import { clearRememberedLogin, getRememberedLogin, rememberProviderAccount } from '../../../../util/remembered-login'
import { googleAuth, msAuth, slackAuth } from '../auth-clients'
import { canSigninWithPlatform, fetchAuthInfo, isInactiveAccountError, readOrganizationName } from '../auth-info'
import { findInactiveAccount, IInactiveAccount } from '../inactive-account'
import { trackLoginStarted, trackSignin } from '../analytics'
import { PLATFORM_BRAND_NAME } from '../platform-brand'
import { DetectedPlatform, NewSigninAction } from '../types'
import { ISigninSession } from './use-signin-session'
import { ISigninModals } from './use-signin-modals'
import { ADP, usePlatformErrorHandler } from './use-platform-error-handler'

export interface IUsePlatformSigninParams {
  localDispatch: Dispatch<NewSigninAction>
  session: ISigninSession
  modals: ISigninModals
  onEmailResolved: (email: string) => void
}

export interface IPlatformSignin {
  /** Microsoft and Google are asked for the account `email` names, when the step has one. */
  signinWith: (platform: DetectedPlatform, email?: string) => void
  connectWithMicrosoft: (interactionRequired?: boolean) => void
  connectWithAdp: (email: string) => Promise<void>
  retrySlackWithFullScopes: () => void
  /** The token the last sign-in with this platform left behind, when it left one. */
  readProviderToken: (platform: DetectedPlatform) => string | undefined
}

/**
 * The provider lookup's status is this account's own, so it decides ahead of the lookup by
 * address, which answers for every account on it. Deactivated accounts are gone from Cognito.
 */
const isDeactivated = (userData: ICheckUserId): boolean => Boolean(userData.userStatus) && userData.userStatus !== 'ACTIVE'

export const usePlatformSignin = ({ localDispatch, session, modals, onEmailResolved }: IUsePlatformSigninParams): IPlatformSignin => {
  const { handlePlatformError } = usePlatformErrorHandler({
    onRetryMicrosoft: () => connectWithMicrosoft(true),
    onCancelled: (platform) => localDispatch({ type: 'bannerRaised', banner: { kind: 'platformCancelled', platform } }),
  })

  /**
   * Every failure ends here, and releasing the form is the half that must never be
   * forgotten: without it the page keeps spinning even though the attempt is over.
   */
  const failPlatformSignin = (error, platform: string) => {
    localDispatch({ type: 'busyChanged', isBusy: false })
    handlePlatformError(error, platform)
  }

  // Captured from the first Slack round so the full-scope retry can target the same
  // workspace. Not rendered, so it does not belong in state.
  const slackTeamIdRef = useRef<string | null>(null)
  // The address the visitor typed, kept so the consent retry asks Microsoft for the same account.
  const microsoftLoginHintRef = useRef<string | undefined>(undefined)
  const startPlatformSignin = (platform: string, payload: Record<string, string> = {}) => {
    localDispatch({ type: 'platformSigninStarted' })
    trackLoginStarted(platform, payload)
  }

  const showInactiveAccount = (email: string, platform: string, inactive: IInactiveAccount) => {
    trackSignin('SIGN_IN_ACCOUNT_INACTIVE', { platform, detectedBy: inactive.detectedBy })
    localDispatch({ type: 'inactiveAccountDetected', email, organizationName: inactive.organizationName })
  }

  /** Returns false when this platform's account on the address is deactivated, having shown the banner. */
  const isPlatformAccountActive = async (email: string, platform: string): Promise<boolean> => {
    const inactive = await findInactiveAccount(email, platform)
    if (inactive) {
      showInactiveAccount(email, platform, inactive)
    }
    return !inactive
  }

  const handleUserData = (userData: ICheckUserId, platform: Platform, otherCreateUserData) => {
    onEmailResolved(userData.mail || '')
    localDispatch({ type: 'busyChanged', isBusy: false })

    // Microsoft and Google report a stopped account as an unknown one, and only they know
    // which company the sign-in landed in: the same address can hold a live account in another
    // company, which is what the address-wide lookup answers about. This status is the answer
    // about this one, so it decides between "you were turned off" and "you are not a member".
    if (isDeactivated(userData)) {
      trackSignin('SIGN_IN_ACCOUNT_INACTIVE', { platform, detectedBy: 'userStatus' })
      localDispatch({
        type: 'inactiveAccountDetected',
        email: userData.mail || '',
        organizationName: userData.organizationName,
      })
      return
    }

    if (userData.type === 'USER_NOT_FOUND' && userData.subscriptionStatus === 'canceled') {
      localDispatch({ type: 'bannerRaised', banner: { kind: 'subscriptionExpired' } })
      return
    }

    if (userData.type === 'USER_NOT_FOUND') {
      localDispatch({
        type: 'accessDenied',
        email: userData.mail || '',
        platform: platform as DetectedPlatform,
        organizationName: userData.organizationName,
      })
      trackSignin('ORG_ALREADY_EXISTS_PAGE_VIEWED', {
        ...omit(userData || {}, ['adminContacts', 'slackUser']),
        platform,
      })
      return
    }

    if (userData.type === 'COMPANY_NOT_FOUND') {
      localStorage.setItem(
        'vtCreateUser',
        JSON.stringify({
          name: userData.name,
          id: `${platform}-${userData.id}`,
          mail: userData.mail,
          platform,
          ...otherCreateUserData,
        })
      )

      // No company answers to this provider account any more, so if it is the one the page
      // opened on, the next visit starts from the address instead of offering it again.
      const remembered = getRememberedLogin()
      if (remembered?.platform === platform && remembered.email.toLowerCase() === (userData.mail || '').toLowerCase()) {
        clearRememberedLogin()
      }

      if (userData.existsOnAnotherPlatform && userData.existingOrganizationName) {
        const orgOnOtherPlatformPayload = { platform, existingOrganizationName: userData.existingOrganizationName }
        trackSignin('ORG_EXISTS_ON_ANOTHER_PLATFORM_OPEN_MODAL', orgOnOtherPlatformPayload)
        trackSignin('SIGN_IN_ORG_EXISTS_ON_ANOTHER_PLATFORM_SHOWN', orgOnOtherPlatformPayload)
        localDispatch({
          type: 'orgOnOtherPlatformDetected',
          email: userData.mail || '',
          platform: userData.existsOnAnotherPlatform,
          organizationName: userData.existingOrganizationName,
        })
        return
      }

      trackSignin('SIGN_IN_NO_ACCOUNT_FOR_PLATFORM', { platform })
      localDispatch({
        type: 'platformAccountNotFound',
        email: userData.mail || '',
        platform: PLATFORM_BRAND_NAME[platform as DetectedPlatform],
      })
      return
    }

    // Nothing in the account explains the failure: the visitor belongs to a known company, so
    // the sign-in reached Cognito and the token was turned down there. That rejection carries
    // no reason of its own (a withdrawn account and a token the provider no longer honours
    // both arrive as "Incorrect username or password"), so the page says what it can instead
    // of stopping the spinner and showing nothing. Reported because the visitor cannot: the
    // attempt looks complete from the outside and never reaches LOGIN_COMPLETED.
    trackSignin('SIGN_IN_PLATFORM_REJECTED', { platform })
    localDispatch({
      type: 'bannerRaised',
      banner: { kind: 'platformSigninRejected', platform: PLATFORM_BRAND_NAME[platform as DetectedPlatform] },
    })
  }

  interface ISlackCreateUserParams {
    userData: ICheckUserId
    slackUserToken: string
    slackOrgTeamId?: string
    slackBotId?: string | null
    slackBotToken?: string | null
    teamName: string | null
  }

  const slackCreateUserData = ({ userData, slackUserToken, slackOrgTeamId, slackBotId = null, slackBotToken = null, teamName }: ISlackCreateUserParams) => ({
    teamName,
    slackBotToken,
    slackUserToken,
    slackOrgTeamId,
    slackBotId,
    isOwner: userData?.slackUser?.isOwner,
    isAdmin: userData?.slackUser?.isAdmin,
    slackTeamId: userData?.slackUser?.slackTeamId,
    imageUrl: userData?.slackUser?.imageUrl,
    slackUserId: userData?.slackUser?.slackUserId,
    timezone: userData?.slackUser?.timezone,
  })

  const connectWithSlack = () => {
    const platform = PlatformEnum.Slack
    startPlatformSignin(platform)
    trackSignin('SIGN_IN_WITH_SLACK_BUTTON_CLICKED', { platform })

    slackAuth
      .connect(null, undefined, {
        // Signed out of Slack, its workspace sign-in cuts the tab off from this page.
        onTabOutOfSight: () => localDispatch({ type: 'bannerRaised', banner: { kind: 'continueInPlatformTab', platform: PLATFORM_BRAND_NAME.slack } }),
      })
      .then(async (response: ISlackOpenidTokenResponse) => {
        // An answer from a cut-off tab finds the form already freed, so it is taken back.
        localDispatch({ type: 'platformSigninStarted' })
        const slackUserToken: string | undefined = response.authed_user.access_token
        if (!slackUserToken) {
          throw new Error('Slack user token is required')
        }
        slackTeamIdRef.current = response.team.id
        slackAuth.setUserToken(slackUserToken)

        let userData: ICheckUserId | undefined = undefined
        try {
          userData = await getSlackUserId(response.authed_user.id, slackUserToken)
          // Slack answered as this account whatever happens next, so the next Slack sign-in will
          // most likely come back as it too, including after this one ends on no access.
          rememberProviderAccount({ email: userData.mail || '', platform })
          if (!userData.vacationTrackerId || isDeactivated(userData)) {
            throw new Error('Handle user data')
          }
          onEmailResolved(userData.mail || '')
          if (userData.mail && !(await isPlatformAccountActive(userData.mail, platform))) {
            return
          }

          let botToken: string | undefined
          try {
            botToken = slackAuth.getBotToken()
          } catch (error) {
            // Bot token is not available, but we can still proceed
          }
          const areScopesValid = await slackAuth.areScopesValid({
            userToken: slackUserToken,
            requiredUserScopes: SLACK_REQUIRED_USER_SCOPES,
            botToken,
            requiredBotScopes: SLACK_REQUIRED_BOT_SCOPES,
          })
          if (!areScopesValid) {
            throw 'missing_scope'
          }

          await session.signinWithPlatformToken({
            username: userData.vacationTrackerId,
            token: slackUserToken,
            platform,
            email: userData.mail || '',
            botToken,
          })
        } catch (error) {
          if (error === 'missing_scope' || error?.response?.data === 'missing_scope') {
            modals.showSlackLoginRequired(true)
            localDispatch({ type: 'busyChanged', isBusy: false })
            return
          }
          if (userData) {
            handleUserData(
              userData,
              platform,
              slackCreateUserData({
                userData,
                slackUserToken,
                slackOrgTeamId: response.enterprise?.id,
                teamName: null,
              })
            )
            return
          }
          // No answer to explain, so the lookup itself failed: said and reported like an OAuth failure.
          failPlatformSignin(error, platform)
        }
      })
      .catch((e) => failPlatformSignin(e, platform))
  }

  /** Second Slack round: the visitor accepted the "log in to Slack" prompt. */
  const retrySlackWithFullScopes = () => {
    // scopes: 'full' separates this second OAuth trip from the first, which reports the same
    // events. Without it a visitor who goes through both looks like two separate attempts.
    startPlatformSignin(PlatformEnum.Slack, { scopes: 'full' })
    slackAuth
      .signin(SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES, slackTeamIdRef.current)
      .then(async (response) => {
        const slackUserToken = response.authed_user.access_token as string
        const botToken: string | undefined = response.access_token

        let userData: ICheckUserId | undefined = undefined
        try {
          slackAuth.setBotToken(botToken)
          slackAuth.setUserToken(slackUserToken)
          modals.showSlackLoginRequired(false)
          userData = await getSlackUserId(response.authed_user.id, slackUserToken)
          rememberProviderAccount({ email: userData.mail || '', platform: PlatformEnum.Slack })
          onEmailResolved(userData.mail || '')
          if (isDeactivated(userData)) {
            throw new Error('Handle user data')
          }
          await session.signinWithPlatformToken({
            username: userData.vacationTrackerId as string,
            token: slackUserToken,
            platform: PlatformEnum.Slack,
            email: userData.mail || '',
            botToken,
          })
        } catch (err) {
          if (userData) {
            handleUserData(
              userData,
              PlatformEnum.Slack,
              slackCreateUserData({
                userData,
                slackUserToken,
                slackOrgTeamId: response.enterprise?.id,
                slackBotId: response.bot_user_id,
                slackBotToken: botToken,
                teamName: response.team.name,
              })
            )
            return
          }
          // No answer to explain, so the lookup itself failed: said and reported like an OAuth failure.
          failPlatformSignin(err, PlatformEnum.Slack)
        }
      })
      .catch((error) => failPlatformSignin(error, PlatformEnum.Slack))
  }

  const connectWithMicrosoft = (interactionRequired = false) => {
    const platform = PlatformEnum.Microsoft
    startPlatformSignin(platform)
    trackSignin('SIGN_IN_WITH_MICROSOFT_BUTTON_CLICKED', { platform })

    // Promises, not async/await: window.open requires a synchronous call chain.
    msAuth
      .signin(['user.readbasic.all', 'team.readbasic.all'], interactionRequired, microsoftLoginHintRef.current)
      .then(async ([tokens, , tenantId]) => {
        const userData: ICheckUserId = await getMsUserId(tenantId, tokens.accessToken)
        onEmailResolved(userData.mail || '')
        if (isDeactivated(userData)) {
          handleUserData(userData, platform, { tenantId, msUserId: userData.id })
          return
        }
        if (userData.vacationTrackerId && userData.mail && !(await isPlatformAccountActive(userData.mail, platform))) {
          return
        }
        if (!userData.id) {
          msAuth.setTokens(tokens)
          handleUserData(userData, platform, { tenantId, msUserId: userData.id })
          return
        }
        try {
          await session.signinWithPlatformToken({
            username: userData.id,
            token: tokens.accessToken,
            platform,
            email: userData.mail || '',
          })
        } catch (error) {
          msAuth.setTokens(tokens)
          handleUserData(userData, platform, { tenantId, msUserId: userData.id })
        }
      })
      .catch((e) => failPlatformSignin(e, platform))
  }

  const connectWithGoogle = (loginHint?: string) => {
    const platform = PlatformEnum.Google
    startPlatformSignin(platform)
    trackSignin('SIGN_IN_WITH_GOOGLE_BUTTON_CLICKED', { platform })

    googleAuth
      .signin(false, loginHint)
      .then(async () => {
        const googleUser = googleAuth.getSignedInUser()
        if (!googleUser.hd) {
          localDispatch({ type: 'bannerRaised', banner: { kind: 'notWorkspaceAccount' } })
          return
        }
        const userData: ICheckUserId = await getGoogleUserId(googleAuth.getIdToken())
        const googleCreateUserData = { googleDomain: googleUser.hd, imageUrl: googleUser.picture, googleUserId: googleUser.sub }
        onEmailResolved(userData.mail || '')
        if (isDeactivated(userData)) {
          handleUserData(userData, platform, googleCreateUserData)
          return
        }
        if (userData.vacationTrackerId && userData.mail && !(await isPlatformAccountActive(userData.mail, platform))) {
          return
        }
        try {
          await session.signinWithPlatformToken({
            username: userData.vacationTrackerId as string,
            token: googleAuth.getAccessToken(),
            platform,
            email: userData.mail || '',
          })
        } catch (error) {
          handleUserData(userData, platform, googleCreateUserData)
        }
      })
      .catch((e) => failPlatformSignin(e, platform))
  }

  const connectWithAdp = async (email: string) => {
    localDispatch({ type: 'platformSigninStarted' })
    onEmailResolved(email)

    // ADP signs in to the email account, which can have been deactivated since the address step.
    let username: string | undefined
    try {
      const response = await fetchAuthInfo(email)
      if (!canSigninWithPlatform(response, PlatformEnum.Email)) {
        showInactiveAccount(email, ADP, { detectedBy: 'availablePlatforms' })
        return
      }
      username = response?.username
      if (!response?.loginOptions?.includes(ADP)) {
        throw new Error('ADP_LOGIN_NOT_ENABLED')
      }
    } catch (error) {
      if (isInactiveAccountError(error)) {
        showInactiveAccount(email, ADP, { detectedBy: 'inactiveError', organizationName: readOrganizationName(error) })
        return
      }
      failPlatformSignin(error, ADP)
      return
    }

    startPlatformSignin(ADP)
    trackSignin('SIGN_IN_WITH_ADP_BUTTON_CLICKED', { platform: ADP })

    const adpAuthorizationUrl =
      'https://accounts.adp.com/auth/oauth/v2/authorize?' +
      'response_type=code&' +
      `client_id=${process.env.REACT_APP_ADP_CLIENT_ID_SSO}&` +
      `redirect_uri=${process.env.REACT_APP_DASHBOARD_URL}/signin&` +
      'scope=openid%20profile'

    const adpConsentUrl =
      'https://adpapps.adp.com/consent-manager/pending/direct?' +
      `consumerApplicationID=${process.env.REACT_APP_ADP_CONSUMER_APPLICATION_ID}&` +
      `successUri=${encodeURIComponent(adpAuthorizationUrl)}`

    BrowserRuntimeV2()
      .openAndWaitForMessage(adpConsentUrl, 'adp-auth')
      .then(async (code) => {
        if (code && username) {
          await session.signinWithPlatformToken({
            username,
            token: code as string,
            platform: ADP,
            email,
          })
        }
        localDispatch({ type: 'busyChanged', isBusy: false })
      })
      .catch((error) => failPlatformSignin(error, ADP))
  }

  const signinWith = (platform: DetectedPlatform, email?: string) => {
    if (platform === 'slack') return connectWithSlack()
    if (platform === 'microsoft') {
      microsoftLoginHintRef.current = email || undefined
      return connectWithMicrosoft()
    }
    return connectWithGoogle(email || undefined)
  }

  // The getters throw when the sign-in stored nothing; absent is an answer here, not a fault.
  const readProviderToken = (platform: DetectedPlatform): string | undefined => {
    try {
      if (platform === 'slack') return slackAuth.getUserToken()
      if (platform === 'google') return googleAuth.getIdToken()
      if (platform === 'microsoft') return msAuth.getTokens().accessToken
    } catch (error) {
      return undefined
    }
    return undefined
  }

  return { signinWith, connectWithMicrosoft, connectWithAdp, retrySlackWithFullScopes, readProviderToken }
}
